/**
 * What a group's score is over (TRA-1367).
 *
 * A group with held-out tests is scored on them alone: `score` and
 * `history` come from the held-out tests, and `splits` carries each
 * split's score and task count. A group with no held-out test is scored
 * over every task and has no `splits`. The api decides; these helpers
 * only say it in words.
 */

import type { GroupSplits } from '@/api/generated/model'
import { formatScore, type ScoreFormat } from './format'

function tests(count: number): string {
  return count === 1 ? 'test' : 'tests'
}

/** The group page's description line. */
export function groupScoreDescription(
  score: number | null,
  splits: GroupSplits | null | undefined,
  format: ScoreFormat,
): string {
  const headline = formatScore(score, format)
  if (!splits) {
    return `Group score ${headline} is the highest score the group has reached.`
  }
  const held = splits.held_out.task_count
  if (held === 0) {
    return `No test of this group is held out, so the group has no score. ${trainingSentence(splits, format)} Pick held-out tests on the Training page.`
  }
  const first = `Group score ${headline} is the highest score reached on the ${held.toString()} held-out ${tests(held)}.`
  return `${first} ${trainingSentence(splits, format)}`
}

function trainingSentence(splits: GroupSplits, format: ScoreFormat): string {
  const { task_count: count, score } = splits.training
  if (count === 0) return 'Every test is held out.'
  const noun = `${count.toString()} training ${tests(count)}`
  if (score === null) return `The ${noun} ${count === 1 ? 'has' : 'have'} no score yet.`
  return `The ${noun} ${count === 1 ? 'scores' : 'score'} ${formatScore(score, format)}.`
}

/** The group card's task count: which tests the score is on, when the group has held-out tests. */
export function groupTaskCountLabel(taskCount: number, splits: GroupSplits | null | undefined): string {
  if (!splits) return `${taskCount.toString()} ${taskCount === 1 ? 'task' : 'tasks'}`
  return `${splits.held_out.task_count.toString()} held out · ${splits.training.task_count.toString()} training`
}

function isScoredOnHeldOut(splits: GroupSplits | null | undefined): boolean {
  return splits !== null && splits !== undefined && splits.held_out.task_count > 0
}

/** The group chart's caption: what its climb is over. */
export function groupHistoryCaption(splits: GroupSplits | null | undefined): string {
  return isScoredOnHeldOut(splits)
    ? "The group's score on its held-out tests at each run date."
    : "The group's score at each run date across all tasks and variants."
}

/** What a variant pick of the group chart rolls up. */
export function groupVariantScope(splits: GroupSplits | null | undefined): string {
  return isScoredOnHeldOut(splits) ? 'every held-out test' : 'every task'
}
